import { useLaden } from '../lib/laden'
import { holeNachbarn, type Nachbar } from '../lib/warteschlangen'

/**
 * Was im Umkreis liegt (§8.4). Zweimal gebraucht und deshalb hier:
 *
 * - bei `place_new`, um zu sehen, ob der Ort schon existiert,
 * - bei einer Dublettenmeldung, um sie überhaupt entscheiden zu können.
 *   26 der wartenden Dublettenmeldungen tragen keinen `related_place_id`;
 *   ohne diese Liste steht dort nur „Dublette" und sonst nichts, worüber sich
 *   urteilen ließe.
 *
 * Mit `auswahl` wird die Liste zur Wahl: Welcher Ort bleibt, wenn zwei
 * zusammengelegt werden?
 */

export interface Auswahl {
  gewaehlt: string | null
  setzen: (ortId: string) => void
}

export default function Nachbarn({
  lat,
  lon,
  ausser = null,
  umkreis = 150,
  ueberschrift = `Nachbarn im Umkreis von ${umkreis} m`,
  leerText = 'Nichts in der Nähe.',
  auswahl,
  zusatz,
}: {
  lat: number
  lon: number
  /** Ein Ort, der nicht als sein eigener Nachbar erscheinen soll. */
  ausser?: string | null
  umkreis?: number
  ueberschrift?: string
  leerText?: string
  auswahl?: Auswahl
  /** Ein ausdrücklich benannter Ort, der außerhalb des Umkreises liegen kann. */
  zusatz?: Nachbar | null
}) {
  const [zustand] = useLaden(() => holeNachbarn(lat, lon, ausser), [lat, lon, ausser])

  const gefunden = zustand.art === 'da' ? zustand.wert : []
  const kandidaten =
    zusatz && !gefunden.some((n) => n.id === zusatz.id)
      ? [zusatz, ...gefunden]
      : gefunden

  return (
    <div className="karte">
      <h3>{ueberschrift}</h3>

      {zustand.art === 'laedt' && <p className="gedaempft">Suche …</p>}
      {zustand.art === 'fehler' && (
        <p className="meldung meldung-fehler">{zustand.meldung}</p>
      )}

      {zustand.art === 'da' &&
        (kandidaten.length === 0 ? (
          <p className="gedaempft">{leerText}</p>
        ) : (
          <>
            {!auswahl && (
              <p className="meldung meldung-warnung">
                {kandidaten.length}{' '}
                {kandidaten.length === 1 ? 'Ort liegt' : 'Orte liegen'} im Umkreis
                von {umkreis} m.
              </p>
            )}
            {auswahl && (
              <p className="leise">Der gemeldete Ort geht im gewählten auf.</p>
            )}

            <ul
              className="nachbarn-liste"
              {...(auswahl ? { role: 'radiogroup', 'aria-label': 'Bleibender Ort' } : {})}
            >
              {kandidaten.map((n) => {
                const zeile = (
                  <>
                    <span className="zahl nachbar-meter">
                      {Number.isFinite(n.meter) ? `${Math.round(n.meter)} m` : '—'}
                    </span>
                    <span>{n.name ?? 'ohne Namen'}</span>
                    <span className="leise">
                      {[n.street, n.city].filter(Boolean).join(', ')}
                      {n.status && n.status !== 'published' && ` · ${n.status}`}
                    </span>
                  </>
                )

                if (!auswahl) {
                  return (
                    <li key={n.id}>
                      <span className="nachbar-zeile">{zeile}</span>
                    </li>
                  )
                }

                return (
                  <li key={n.id}>
                    <label
                      className={
                        auswahl.gewaehlt === n.id ? 'nachbar-wahl aktiv' : 'nachbar-wahl'
                      }
                    >
                      <input
                        type="radio"
                        name="bleibender-ort"
                        checked={auswahl.gewaehlt === n.id}
                        onChange={() => auswahl.setzen(n.id)}
                      />
                      {zeile}
                    </label>
                  </li>
                )
              })}
            </ul>
          </>
        ))}
    </div>
  )
}
